import { useCallback, useEffect, useState } from 'react';

import { useAuthentication } from '@/hooks/user/useAuthentication';
import { useInAppNotificationNavigation } from '@/hooks/inAppNotification/useInAppNotificationNavigation';
import { IN_APP_NOTIFICATIONS_QUERY_KEY } from '@/hooks/inAppNotification/useInAppNotifications';
import { IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY } from '@/hooks/inAppNotification/useInAppNotificationsPage';
import { useInAppNotificationsAvailable } from '@/hooks/inAppNotification/useInAppNotificationsAvailable';
import { useMarkInAppNotificationSeen } from '@/hooks/inAppNotification/useMarkInAppNotificationSeen';
import { queryClient } from '@/resources/queryClient';
import { acquireInAppNotificationSocket } from '@/utils/inAppNotificationSocket';
import {
  announceInAppNotifications,
  liveNotificationBuffer,
  mergeInAppNotifications,
  normaliseSocketNotification,
  resetAnnouncedInAppNotifications,
} from '@/utils/inAppNotification';
import type { InAppNotificationSocketStatus } from '@/utils/inAppNotificationSocket';
import type { InAppNotificationSocketContextValue } from '@/contexts/inAppNotification';
import type {
  InAppNotification,
  InAppNotificationSocketPayload,
  InAppNotificationsData,
} from '@/types/inAppNotification';

/**
 * Owns the app's notification socket for as long as it is mounted.
 *
 * The socket is a delivery signal only — react-query stays the single source of
 * truth for what is rendered, and live events are merged into the same cache
 * entry the backfill populates.
 */
export const useInAppNotificationSocket = (): InAppNotificationSocketContextValue => {
  const isAvailable = useInAppNotificationsAvailable();
  const { user: authUser } = useAuthentication();
  const navigateToNotification = useInAppNotificationNavigation();
  const { markSeen } = useMarkInAppNotificationSeen();

  const [status, setStatus] = useState<InAppNotificationSocketStatus | 'disabled'>('disabled');

  const handleView = useCallback((notification: InAppNotification) => {
    markSeen(notification.id);
    navigateToNotification(notification);
  }, [markSeen, navigateToNotification]);

  const userId = authUser?.gsUid;

  // TEMPORARY: the notifications socket still authenticates with the Firebase id
  // token, which is null since the Envoy-session migration, so it cannot connect.
  // Kept off until the socket moves to cookie auth (see specs/envoy-auth-migration.md).
  // Flip to `true` to re-enable.
  const inAppNotificationsSocketEnabled = false;
  const isEnabled = inAppNotificationsSocketEnabled
    && isAvailable
    && Boolean(userId);

  const handleNotification = useCallback((payload: InAppNotificationSocketPayload) => {
    const notification = normaliseSocketNotification(payload);

    if (!notification) {
      return;
    }

    liveNotificationBuffer.record(notification);

    const current = queryClient.getQueryData<InAppNotificationsData>(IN_APP_NOTIFICATIONS_QUERY_KEY);
    const { data, added } = mergeInAppNotifications(current, [notification]);

    queryClient.setQueryData<InAppNotificationsData>(IN_APP_NOTIFICATIONS_QUERY_KEY, data);

    // An open page of the all-notifications view is a fixed slice and is not
    // spliced into; it is only marked stale, so the next page or filter change
    // fetches a history that includes this one.
    queryClient.invalidateQueries({
      queryKey: IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY,
      refetchType: 'none',
    });

    // Announce only what the cache did not already hold, so a duplicate
    // broadcast of the same row does not toast twice.
    announceInAppNotifications(added, handleView);
  }, [handleView]);

  const handleJoined = useCallback(() => {
    // Channels have no replay, so a rejoin means an unknown gap. The backfill
    // query is the only thing that can recover what was broadcast during it.
    //
    // This runs on the first join too, which looks redundant next to the query's
    // own fetch on mount but is not: that response is built before the join
    // completes, and a notification broadcast in between would appear in neither
    // it nor the socket.
    queryClient.invalidateQueries({ queryKey: IN_APP_NOTIFICATIONS_QUERY_KEY });
  }, []);

  useEffect(() => {
    if (!isEnabled || !userId) {
      setStatus('disabled');

      return;
    }

    resetAnnouncedInAppNotifications();
    liveNotificationBuffer.reset();

    const release = acquireInAppNotificationSocket(userId, {
      onNotification: handleNotification,
      onJoined: handleJoined,
      onStatusChange: setStatus,
    });

    return release;
  }, [
    isEnabled,
    userId,
    handleNotification,
    handleJoined,
  ]);

  return { status };
};
